// ============================================================================
// Chemicals on site — add, edit and remove, in place on the facility card.
//
// 2026-09-09, facility-chemicals-in-app. Ben: "We do not use that form. It
// does not exist or function. We will not build another form. We are now in
// the DO IT IN THE APP phase." So: no modal, no wizard, no survey. A row of
// controls inside the section that already showed the list.
//
// The one question this answers is WHICH CLINIC IS USING WHICH CHEMICAL.
// Nobody re-orders from this data, so there are no quantities, no par levels
// and no reorder points here on purpose.
//
// Every write goes through src/shared/buildings/facility-chemicals.ts, which
// the AM Collect review screen also calls. There is one save path, not two.
// ============================================================================

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Edit2, Plus, Trash2 } from 'lucide-react';
import { SmartSelect, type SmartSelectRow } from '../../../components/smart-select';
import { Button, EmptyState, ErrorState, Skeleton } from '../../../shared/components';
import { useAuth } from '../../../shared/auth';
import { registerReader, WriteDidNotLandError } from '../../../shared/save-path';
import { errorMessage } from '../../../shared/errors';
import { fmtBareDate } from '../../../shared/format';
import {
  ChemicalAlreadyOnBuilding,
  MANAGED_BY_CHOICES,
  addFacilityChemical,
  fetchFacilityChemicals,
  fetchRecorderNames,
  managedByLabel,
  removeFacilityChemical,
  updateFacilityChemical,
  type ChemicalManagedBy,
  type FacilityChemical,
} from '../../../shared/buildings/facility-chemicals';

/** Whatever threw, the sentence the person reads. */
function saveMessage(err: unknown): string {
  if (err instanceof WriteDidNotLandError) return err.described.message;
  if (err instanceof ChemicalAlreadyOnBuilding) return err.message;
  return errorMessage(err, 'Could not save that.');
}

/** A retired catalogue row is never offered — the pool behind a saved row
 *  stays whole, so an existing choice still shows its name (TCSD-0163). */
function isPickable(row: SmartSelectRow): boolean {
  return row.raw?.is_active !== false;
}

const SUPPLIER_CLASS =
  'w-full rounded-lg border border-tcs-border bg-tcs-surface px-2 py-1.5 text-sm text-tcs-text';

interface DraftState {
  supplyId: string | null;
  typedName: string;
  offList: boolean;
  managedBy: ChemicalManagedBy | '';
  notes: string;
}

const EMPTY_DRAFT: DraftState = {
  supplyId: null,
  typedName: '',
  offList: false,
  managedBy: '',
  notes: '',
};

export function FacilityChemicalsPanel({ facility_id }: { facility_id: string }) {
  const { employee } = useAuth();
  const byEid = employee?.employee_id ?? null;

  const [rows, setRows] = useState<FacilityChemical[]>([]);
  const [names, setNames] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState<DraftState>(EMPTY_DRAFT);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [edit, setEdit] = useState<{ managedBy: ChemicalManagedBy | ''; notes: string; typedName: string }>({
    managedBy: '',
    notes: '',
    typedName: '',
  });

  const refetch = useCallback(() => setTick((n) => n + 1), []);
  // A write anywhere in the app that touches these tables re-reads this list.
  useEffect(
    () => registerReader(['facility_chemicals', 'supplies'], refetch),
    [refetch],
  );

  useEffect(() => {
    let cancelled = false;
    async function load() {
      // ⛔ loading flips on the FIRST load only — a refetch must not unmount
      // the row somebody is editing (TCSD-0039).
      setError(null);
      try {
        const list = await fetchFacilityChemicals(facility_id);
        if (cancelled) return;
        setRows(list);
        const eids = list.map((r) => r.recorded_by_eid).filter((x): x is string => !!x);
        if (eids.length > 0) {
          const map = await fetchRecorderNames(eids);
          if (!cancelled) setNames(map);
        }
      } catch (e) {
        if (cancelled) return;
        console.error('[facility-chemicals] load failed', e);
        setError(errorMessage(e, 'Could not load the chemicals on this building.'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [facility_id, tick]);

  const unmatchedCount = useMemo(() => rows.filter((r) => r.unmatched).length, [rows]);

  const canSaveDraft = draft.offList
    ? draft.typedName.trim().length > 0
    : !!draft.supplyId;

  async function onAdd() {
    setSaving(true);
    setSaveError(null);
    try {
      await addFacilityChemical({
        facilityId: facility_id,
        supplyId: draft.offList ? null : draft.supplyId,
        typedName: draft.offList ? draft.typedName : null,
        managedBy: draft.managedBy === '' ? null : draft.managedBy,
        notes: draft.notes,
        byEid,
        source: 'Manual',
      });
      setDraft(EMPTY_DRAFT);
      setAdding(false);
      refetch();
    } catch (e) {
      // Keep the row on screen with what they typed still in it (TCSD-0044).
      setSaveError(saveMessage(e));
    } finally {
      setSaving(false);
    }
  }

  async function onSaveEdit(row: FacilityChemical) {
    setSaving(true);
    setSaveError(null);
    try {
      await updateFacilityChemical({
        fcId: row.fc_id,
        managedBy: edit.managedBy === '' ? null : edit.managedBy,
        notes: edit.notes,
        ...(row.unmatched ? { typedName: edit.typedName } : {}),
        byEid,
      });
      setEditingId(null);
      refetch();
    } catch (e) {
      setSaveError(saveMessage(e));
    } finally {
      setSaving(false);
    }
  }

  async function onRemove(row: FacilityChemical) {
    // One click, no dialogue: it is reversible and fully audited, and adding it
    // back restores this same row.
    setSaveError(null);
    try {
      await removeFacilityChemical(row.fc_id);
      refetch();
    } catch (e) {
      setSaveError(saveMessage(e));
    }
  }

  if (loading) return <Skeleton width="100%" height={120} block />;
  if (error) {
    return (
      <ErrorState
        title="Couldn't load the chemicals"
        body={error}
        onRetry={refetch}
      />
    );
  }

  return (
    <div className="rounded-xl border border-tcs-border bg-tcs-bg p-4 sm:p-5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-tcs-text">
          Chemicals on site
          <span className="ml-2 text-xs font-normal text-tcs-text-muted">
            ({rows.length}
            {unmatchedCount > 0 ? `, ${unmatchedCount} not on the list` : ''})
          </span>
        </h3>
        {!adding && (
          <Button
            variant="secondary"
            size="sm"
            icon={<Plus className="h-4 w-4" />}
            onClick={() => {
              setAdding(true);
              setSaveError(null);
            }}
          >
            Add a chemical
          </Button>
        )}
      </div>

      {adding && (
        <div className="mt-3 space-y-2 rounded-lg border border-tcs-border bg-tcs-surface p-3">
          {!draft.offList ? (
            <SmartSelect
              entity="supply"
              entityLabel="chemical"
              scope={{ category: 'CHM' }}
              value={draft.supplyId}
              onChange={(id) => setDraft((d) => ({ ...d, supplyId: id }))}
              isSelectable={isPickable}
              placeholder="Search chemicals…"
            />
          ) : (
            <input
              className={SUPPLIER_CLASS}
              placeholder="Type the product name"
              value={draft.typedName}
              onChange={(e) => setDraft((d) => ({ ...d, typedName: e.target.value }))}
            />
          )}

          <button
            type="button"
            className="text-xs font-medium text-tcs-text-secondary underline"
            onClick={() =>
              setDraft((d) => ({
                ...d,
                offList: !d.offList,
                supplyId: null,
                typedName: '',
              }))
            }
          >
            {draft.offList ? 'Pick from the list instead' : 'Not on this list'}
          </button>

          <div className="grid gap-2 sm:grid-cols-2">
            <label className="block">
              <span className="mb-1 block text-xs text-tcs-text-muted">Who supplies it</span>
              <select
                className={SUPPLIER_CLASS}
                value={draft.managedBy}
                onChange={(e) =>
                  setDraft((d) => ({
                    ...d,
                    managedBy: e.target.value as ChemicalManagedBy | '',
                  }))
                }
              >
                {MANAGED_BY_CHOICES.map((c) => (
                  <option key={c.value || 'unknown'} value={c.value}>
                    {c.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="mb-1 block text-xs text-tcs-text-muted">
                Anything worth saying (optional)
              </span>
              <input
                className={SUPPLIER_CLASS}
                placeholder="portable dispenser on top of bottle"
                value={draft.notes}
                onChange={(e) => setDraft((d) => ({ ...d, notes: e.target.value }))}
              />
            </label>
          </div>

          <div className="flex flex-wrap items-center gap-2 pt-1">
            <Button size="sm" onClick={onAdd} disabled={!canSaveDraft || saving}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                setAdding(false);
                setDraft(EMPTY_DRAFT);
                setSaveError(null);
              }}
              disabled={saving}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}

      {saveError && (
        <p className="mt-3 rounded-lg border border-tcs-border bg-tcs-surface p-3 text-sm text-tcs-text">
          {saveError}
        </p>
      )}

      {rows.length === 0 && !adding && (
        <div className="mt-3">
          <EmptyState
            title="No chemicals recorded here yet."
            body="Anyone at TCS can record what is on site at any time — add the first one above."
          />
        </div>
      )}

      {rows.length > 0 && (
        <ul className="mt-3 divide-y divide-tcs-border">
          {rows.map((r) => {
            const editing = editingId === r.fc_id;
            return (
              <li key={r.fc_id} className="py-2.5">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-sm text-tcs-text">
                      {r.display_name}
                      {r.unmatched && (
                        <span className="ml-2 rounded-full border border-tcs-border px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-tcs-text-muted">
                          not on the list
                        </span>
                      )}
                    </p>
                    <p className="text-xs text-tcs-text-muted">
                      {managedByLabel(r.managed_by)}
                      {r.notes ? ` · ${r.notes}` : ''}
                    </p>
                    <p className="text-[11px] text-tcs-text-muted">
                      {r.recorded_by_eid
                        ? `Recorded by ${names[r.recorded_by_eid] ?? r.recorded_by_eid}`
                        : 'Recorded'}
                      {r.recorded_at ? ` on ${fmtBareDate(r.recorded_at)}` : ''}
                    </p>
                  </div>
                  <div className="flex shrink-0 items-center gap-1">
                    <button
                      type="button"
                      aria-label={`Edit ${r.display_name}`}
                      className="rounded-lg border border-tcs-border p-1.5 text-tcs-text-secondary"
                      onClick={() => {
                        setEditingId(editing ? null : r.fc_id);
                        setSaveError(null);
                        setEdit({
                          managedBy: r.managed_by ?? '',
                          notes: r.notes ?? '',
                          typedName: r.supply_name ?? '',
                        });
                      }}
                    >
                      <Edit2 className="h-3.5 w-3.5" />
                    </button>
                    <button
                      type="button"
                      aria-label={`Remove ${r.display_name}`}
                      className="rounded-lg border border-tcs-border p-1.5 text-tcs-text-secondary"
                      onClick={() => onRemove(r)}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>

                {editing && (
                  <div className="mt-2 space-y-2 rounded-lg border border-tcs-border bg-tcs-surface p-3">
                    {r.unmatched && (
                      <label className="block">
                        <span className="mb-1 block text-xs text-tcs-text-muted">Product name</span>
                        <input
                          className={SUPPLIER_CLASS}
                          value={edit.typedName}
                          onChange={(e) => setEdit((s) => ({ ...s, typedName: e.target.value }))}
                        />
                      </label>
                    )}
                    <div className="grid gap-2 sm:grid-cols-2">
                      <label className="block">
                        <span className="mb-1 block text-xs text-tcs-text-muted">Who supplies it</span>
                        <select
                          className={SUPPLIER_CLASS}
                          value={edit.managedBy}
                          onChange={(e) =>
                            setEdit((s) => ({
                              ...s,
                              managedBy: e.target.value as ChemicalManagedBy | '',
                            }))
                          }
                        >
                          {MANAGED_BY_CHOICES.map((c) => (
                            <option key={c.value || 'unknown'} value={c.value}>
                              {c.label}
                            </option>
                          ))}
                        </select>
                      </label>
                      <label className="block">
                        <span className="mb-1 block text-xs text-tcs-text-muted">
                          Anything worth saying (optional)
                        </span>
                        <input
                          className={SUPPLIER_CLASS}
                          value={edit.notes}
                          onChange={(e) => setEdit((s) => ({ ...s, notes: e.target.value }))}
                        />
                      </label>
                    </div>
                    <div className="flex flex-wrap items-center gap-2 pt-1">
                      <Button size="sm" onClick={() => onSaveEdit(r)} disabled={saving}>
                        {saving ? 'Saving…' : 'Save'}
                      </Button>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => setEditingId(null)}
                        disabled={saving}
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {unmatchedCount > 0 && (
        <p className="mt-3 text-xs text-tcs-text-muted">
          {unmatchedCount === 1
            ? 'One chemical here was typed in and has no catalogue match yet.'
            : `${unmatchedCount} chemicals here were typed in and have no catalogue match yet.`}{' '}
          They are listed for review at Supply → Chemicals.
        </p>
      )}
    </div>
  );
}

export default FacilityChemicalsPanel;
